'use client'

import React, { useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { useTranslations } from 'next-intl'

interface IFAQItemData {
  question: string
  answer: React.ReactNode
}

/**
 * FAQ accordion item component
 */
function FAQItem({
  question,
  answer,
}: {
  question: string
  answer: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="group border-b border-white/5 last:border-none">
      <button
        type="button"
        onClick={() => {
          return setIsOpen(!isOpen)
        }}
        className="w-full py-8 flex items-start justify-between text-left focus:outline-none"
      >
        <span
          className={`text-lg md:text-xl font-medium transition-all duration-300 ${
            isOpen
              ? 'text-gold translate-x-2'
              : 'text-white group-hover:text-gray-300'
          }`}
        >
          {question}
        </span>
        <span
          className={`ml-4 p-2 rounded-full border border-white/10 transition-all duration-300 ${
            isOpen
              ? 'bg-gold text-black border-gold rotate-180 shadow-[0_0_15px_#FFD700]'
              : 'bg-transparent text-gray-400 group-hover:border-white/30'
          }`}
        >
          {isOpen ? (
            <Minus className="w-4 h-4" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </span>
      </button>

      <div
        className={`grid transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] ${
          isOpen
            ? 'grid-rows-[1fr] opacity-100 pb-8'
            : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="text-gray-400 leading-loose font-light text-base md:text-lg pl-2 border-l-2 border-gold/30 ml-1">
            {answer}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * FAQ section component with accordion functionality
 */
export function FAQ() {
  const t = useTranslations('home_v3.faq')

  const faqData: IFAQItemData[] = [
    {
      question: t('items.what_is.question'),
      answer: t('items.what_is.answer'),
    },
    {
      question: t('items.good_for_smb.question'),
      answer: t('items.good_for_smb.answer'),
    },
    {
      question: t('items.tech_skills.question'),
      answer: t('items.tech_skills.answer'),
    },
    {
      question: t('items.website_types.question'),
      answer: t('items.website_types.answer'),
    },
    {
      question: t('items.seo_tools.question'),
      answer: t('items.seo_tools.answer'),
    },
    {
      question: t('items.custom_domain.question'),
      answer: t('items.custom_domain.answer'),
    },
    {
      question: t('items.hosting.question'),
      answer: t('items.hosting.answer'),
    },
    {
      question: t('items.updates.question'),
      answer: t('items.updates.answer'),
    },
    {
      question: t('items.crm.question'),
      answer: t('items.crm.answer'),
    },
    {
      question: t('items.email.question'),
      answer: t('items.email.answer'),
    },
    {
      question: t('items.branding.question'),
      answer: t('items.branding.answer'),
    },
    {
      question: t('items.payments.question'),
      answer: t('items.payments.answer'),
    },
    {
      question: t('items.ai_features.question'),
      answer: t('items.ai_features.answer'),
    },
    {
      question: t('items.mobile_friendly.question'),
      answer: t('items.mobile_friendly.answer'),
    },
    {
      question: t('items.security.question'),
      answer: t('items.security.answer'),
    },
    {
      question: t('items.build_time.question'),
      answer: t('items.build_time.answer'),
    },
    {
      question: t('items.other_features.question'),
      answer: t('items.other_features.answer'),
    },
    {
      question: t('items.support.question'),
      answer: (
        <>
          {t('items.support.answer_part1')}{' '}
          <a
            href={t('items.support.answer_link')}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gold hover:underline"
          >
            {t('items.support.answer_link')}
          </a>{' '}
          {t('items.support.answer_part2')}
        </>
      ),
    },
  ]

  return (
    <div className="w-full max-w-[900px] mx-auto px-6 py-32">
      <div className="text-center mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tighter">
          {t('title_part1')}{' '}
          <span className="text-transparent bg-clip-text bg-gold-gradient">
            {t('title_part2')}
          </span>
        </h2>
      </div>

      <div className="relative">
        <div className="absolute inset-0 bg-gold/5 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="relative bg-card/50 backdrop-blur-xl border border-white/5 rounded-[40px] p-8 md:p-16 shadow-2xl">
          {faqData.map((item, index) => {
            return (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
